import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/shared/store";
import type { AuthUser } from "../types";
import {
  encryptedGetItem,
  encryptedRemoveItem,
} from "@/shared/lib/encryptedStorage";

/* ═══════════════════════════════════════════════════════════════
   Constantes
═══════════════════════════════════════════════════════════════ */
const USER_KEY = "evoramaxx_user";

/* ═══════════════════════════════════════════════════════════════
   Async thunk — hidrata o user do localStorage criptografado
═══════════════════════════════════════════════════════════════ */
export const hydrateUser = createAsyncThunk("auth/hydrateUser", async () => {
  // Limpa chaves legadas (tokens que estavam em localStorage)
  localStorage.removeItem("evoramaxx_access_token");
  localStorage.removeItem("evoramaxx_refresh_token");
  localStorage.removeItem("evoramaxx_token");

  const user = await encryptedGetItem<AuthUser>(USER_KEY);
  return user;
});

/* ═══════════════════════════════════════════════════════════════
   State
═══════════════════════════════════════════════════════════════ */
interface AuthState {
  user: AuthUser | null;
  userLoaded: boolean;
}

const initialState: AuthState = {
  user: null,
  userLoaded: false,
};

/* ═══════════════════════════════════════════════════════════════
   Slice
═══════════════════════════════════════════════════════════════ */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.userLoaded = true;
    },
    clearUser(state) {
      state.user = null;
      encryptedRemoveItem(USER_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.userLoaded = true;
      })
      .addCase(hydrateUser.rejected, (state) => {
        state.user = null;
        state.userLoaded = true;
      });
  },
});

export const { setUser, clearUser } = authSlice.actions;
export const authReducer = authSlice.reducer;

/* ═══════════════════════════════════════════════════════════════
   Selectors
═══════════════════════════════════════════════════════════════ */
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => !!state.auth.user;
export const selectUserLoaded = (state: RootState) => state.auth.userLoaded;

/* ═══════════════════════════════════════════════════════════════
   Persistência — chamado externamente (authApi) após setUser
═══════════════════════════════════════════════════════════════ */
export { USER_KEY };
